/* tslint:disable:no-identical-functions */
import { assign, get, isArray, isNil, isString } from 'lodash';

import { MobizErrorType } from '../other/mobiz-error.type';

import { SerializableObject } from './serializable-object';

/**
 * Interface representing the handled exceptions returned by the API.
 *
 * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.GlobalApiExceptionFilter.OnException()
 */
export interface IMobizHttpApiErrorResponse {
  errorType: string;
  errorCode?: string;
  details?: string;
  message?: string;
  trackingId?: string;
  errorReference?: string;
}

/**
 * An interface for storing extra properties that do not exist in MobizHttpApiErrorResponse;
 *
 * This is a workaround for the fact that not all API error responses return the same data format.
 */
export interface IMobizHttpApiErrorResponseExtras extends Record<string, unknown> {
}

/**
 * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.GlobalApiExceptionFilter.OnException()
 */
export class MobizHttpApiErrorResponse extends SerializableObject implements IMobizHttpApiErrorResponse {
  /**
   * Contains a list of properties that exist on MobizHttpApiErrorResponse, all other properties
   * will be added in the extras record.
   */
  public static readonly VALID_PROPERTY_NAMES: (keyof IMobizHttpApiErrorResponse)[] = [
    'details',
    'errorCode',
    'errorReference',
    'errorType',
    'message',
    'trackingId',
  ];

  public errorType: MobizErrorType | string;
  public errorCode?: string;
  public details?: string;
  public message?: string;
  public trackingId?: string | 'Disabled';
  public errorReference?: string | 'Disabled';

  /**
   * A record containing extra values that do not map MobizHttpApiErrorResponse;
   *
   * This is a workaround for the fact that not all API error responses return the same data format.
   */
  public extras?: IMobizHttpApiErrorResponseExtras;

  // tslint:disable-next-line:no-any
  public buildFromJSON(json: any): void {
    if (isNil(json)) {
      return;
    }
    if (json instanceof MobizHttpApiErrorResponse) {
      assign(this, { ...json });
      return;
    }
    try {
      const data: Partial<IMobizHttpApiErrorResponse> & { extras: IMobizHttpApiErrorResponseExtras } = { extras: {} };
      if (isString(json)) {
        data.extras = { error: json };
      } else if (isArray(json)) {
        data.extras = { errors: json };
      } else {
        Object.keys(json).forEach((propertyName: string): void => {
          const propertyValue = json[propertyName];
          if (isNil(propertyValue)) {
            return;
          }
          if (this.isValidPropertyName(propertyName) && typeof propertyValue === 'string') {
            data[propertyName] = propertyValue;
          } else {
            // Store all unknown data in the extras key.
            // This is a workaround for the fact that not all API error responses return the same
            // data format. Most notably, the OAuth service doesn't fallow the pattern.
            data.extras[propertyName] = propertyValue;
          }
        });
      }
      assign(this, data);
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Return a name for use in error dialogs and pages.
   */
  public getName(): string {
    // Preferred fields are at the top, then fields with more details. This prevents empty error dialogs.
    const candidatePaths: string[] = [
      'errorCode',
      'errorType',
      // NOTE: The error and error_description are returned by the OAuth service.
      'extras.error',
      'extras.error_description',
    ];
    return this.getValueFromCandidates(candidatePaths) || 'Unknown error';
  }

  /**
   * Return a message for use in error dialogs and pages.
   *
   * NOTE: The API doesn't always include a message property, see GlobalApiExceptionFilter on the backend.
   *
   * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.GlobalApiExceptionFilter.OnException()
   */
  public getMessage(): string {
    // Preferred fields are at the top, then fields with more details. This prevents empty error dialogs.
    const candidatePaths: string[] = [
      'message',
      // NOTE: The error and error_description are returned by the OAuth service.
      'extras.error_description',
      'extras.error',
      'errorType',
      'errorCode'
    ];
    return this.getValueFromCandidates(candidatePaths) || 'Unknown error';
  }

  /**
   * Method for checking if the response is an OAuth error.
   *
   * NOTE: This doesn't work on OAuth specific API endpoints, they return different errors.
   *
   * @see isOAuthApiEndpoint()
   * @see AuthService
   * @see OAuthLoginError
   * @see TokenRefreshService
   * @see OAuthTokenRefreshError
   *
   * @see mobiz-backend:Mobiz.Web.Core.Security.CustomOAuthProvider.HandleTokenRequest()
   * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.ValidationExceptionHandler
   * @see mobiz-backend:Mobiz.Core.ErrorHandling.ValidationException
   */
  public isOAuthError(): boolean {
    return this.errorType === 'OAuth';
  }

  /**
   * Method for checking if the response is a Validation error.
   *
   * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.DkValidationErrorHandler
   * @see mobiz-backend:Mobiz.Web.Core.ErrorHandling.ValidationExceptionHandler
   * @see mobiz-backend:Mobiz.Core.ErrorHandling.ValidationException
   */
  public isValidationError(): boolean {
    return this.errorType === 'Validation';
  }

  /**
   * Method for checking if the response is a NoToken logout error.
   *
   * @see mobiz-backend:Mobiz.Core.SecurityRepository.DeleteApiToken()
   * @see mobiz-backend:Mobiz.Core.MobizCoreErrorCodes.NoToken
   */
  public isNoTokenError(): boolean {
    return this.errorType === 'InvalidArgument' && this.errorCode === 'NoToken';
  }

  private getValueFromCandidates(candidatePaths: string[]): string | undefined {
    for (const candidatePath of candidatePaths) {
      let message: unknown = get(this, candidatePath);
      if (typeof message === 'string' && message.length) {
        return candidatePath === 'errorCode' ? `ErrorCode: ${message}` : message;
      }
    }
    return undefined;
  }

  private isValidPropertyName(name: string): name is keyof IMobizHttpApiErrorResponse {
    return (MobizHttpApiErrorResponse.VALID_PROPERTY_NAMES as string[]).includes(name);
  }
}
